import React, { useEffect, useState } from "react";
import { SerializedCondition } from "../types";
import useApi from "@/hooks/useApi";

export type DeploymentCondition = {
  deploymentIds: string[];
};

interface DeploymentOption {
  deploymentId: string;
  displayName: string;
}

export function DeploymentEditor({
  condition,
  onUpdate
}: {
  condition: SerializedCondition;
  onUpdate: (condition: SerializedCondition) => void;
}) {
  const deploymentCondition = JSON.parse(
    condition.data || '{"deploymentIds":[]}'
  ) as DeploymentCondition;

  const [deployments, setDeployments] = useState<DeploymentOption[]>([]);
  const api = useApi();

  useEffect(() => {
    async function load() {
      try {
        const res = await api.messaging.get("Deployment");
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : [];
          setDeployments(
            list
              .filter((d: DeploymentOption) => d.deploymentId)
              .map((d: DeploymentOption) => ({
                deploymentId: d.deploymentId,
                displayName: d.displayName || d.deploymentId
              }))
          );
        }
      } catch {
        // keep empty
      }
    }
    load();
  }, [api.messaging]); // eslint-disable-line react-hooks/exhaustive-deps

  function update(data: DeploymentCondition) {
    onUpdate({ ...condition, data: JSON.stringify(data) });
  }

  function toggleDeployment(deploymentId: string, checked: boolean) {
    const current = deploymentCondition.deploymentIds ?? [];
    const next = checked
      ? [...current, deploymentId]
      : current.filter((id) => id !== deploymentId);
    update({ deploymentIds: next });
  }

  const selected = deploymentCondition.deploymentIds ?? [];

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-slate-500">
        Match when the visitor clicked one of these chat buttons:
      </p>
      {deployments.length === 0 ? (
        <p className="text-xs text-slate-400 italic">Loading deployments…</p>
      ) : (
        <div className="flex flex-col gap-1 max-h-48 overflow-y-auto border border-slate-200 rounded-md p-2">
          {deployments.map((d) => (
            <label
              key={d.deploymentId}
              className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5"
            >
              <input
                type="checkbox"
                checked={selected.includes(d.deploymentId)}
                onChange={(e) => toggleDeployment(d.deploymentId, e.target.checked)}
                className="cursor-pointer"
              />
              <span className="text-slate-700">{d.displayName}</span>
              <code className="text-[10px] font-mono text-slate-400 ml-auto shrink-0">
                {d.deploymentId}
              </code>
            </label>
          ))}
        </div>
      )}
      {selected.length === 0 && (
        <p className="text-xs text-amber-600">Select at least one chat button.</p>
      )}
    </div>
  );
}
